import { RefObject, useEffect, useRef } from 'react';
import { useBrowser } from '../services/BrowserService';

export const useEventListener = <
  KW extends keyof WindowEventMap,
  KH extends keyof HTMLElementEventMap,
  T extends HTMLElement
>(
  eventName: KW | KH,
  handler: (event: WindowEventMap[KW] | HTMLElementEventMap[KH] | Event) => void,
  element?: RefObject<T>,
) => {
  // Create a ref that stores handler
  const savedHandler = useRef<typeof handler>();
  const browser = useBrowser();

  useEffect(() => {
    // Define the listening target
    const targetElement: T | Window | undefined = element?.current || browser.window;

    if (!targetElement || !targetElement.addEventListener) return;

    // Update saved handler if necessary
    if (savedHandler.current !== handler) {
      savedHandler.current = handler;
    }

    // Create event listener that calls handler function stored in ref
    const eventListener: typeof handler = event => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!savedHandler?.current) {
        savedHandler.current(event);
      }
    };

    targetElement.addEventListener(eventName, eventListener);

    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, handler, browser.window]);
};
